import type { MarkerDef } from './types'

/**
 * Marcadores laboratoriais da metodologia Mãe Nutrida para GESTANTES.
 * Fonte: planilha `Exames Laboratoriais - Gestação.xlsx`.
 *
 * As faixas verdes ("Valor Ideal") variam por trimestre quando a planilha
 * marca explicitamente `1° tri:`, `2° tri:`, `3° tri:`. Caso contrário a
 * mesma faixa é replicada em TRIM1/TRIM2/TRIM3 para simplificar a UI.
 */
export const GESTANTE_MARKERS: MarkerDef[] = [
  {
    id: 'hemacias',
    name: 'Hemácias',
    group: 'Eritrograma e Leucograma',
    ranges: {
      TRIM1: { min: 4, max: 5.5, display: '4-5,5milhões/mm³', unit: 'milhões/mm³' },
      TRIM2: { min: 4, max: 5.5, display: '4-5,5milhões/mm³', unit: 'milhões/mm³' },
      TRIM3: { min: 4, max: 5.5, display: '4-5,5milhões/mm³', unit: 'milhões/mm³' },
    },
  },
  {
    id: 'hemoglobina',
    name: 'Hemoglobina',
    group: 'Eritrograma e Leucograma',
    obs: 'acompanhar por trimestre',
    ranges: {
      TRIM1: { min: 12.6, display: '>12,6g/dL', unit: 'g/dL' },
      TRIM2: { min: 11, max: 12, display: '11-12g/dL', unit: 'g/dL' },
      TRIM3: { min: 11, display: '>11g/dL', unit: 'g/dL' },
    },
  },
  {
    id: 'hematocrito',
    name: 'Hematócrito',
    group: 'Eritrograma e Leucograma',
    ranges: {
      TRIM1: { min: 39, display: '>39%', unit: '%' },
      TRIM2: { min: 39, display: '>39%', unit: '%' },
      TRIM3: { min: 39, display: '>39%', unit: '%' },
    },
  },
  {
    id: 'hcm',
    name: 'HCM',
    group: 'Eritrograma e Leucograma',
    ranges: {
      TRIM1: { min: 27, max: 33, display: '27 - 33pg', unit: 'pg' },
      TRIM2: { min: 27, max: 33, display: '27 - 33pg', unit: 'pg' },
      TRIM3: { min: 27, max: 33, display: '27 - 33pg', unit: 'pg' },
    },
  },
  {
    id: 'vcm',
    name: 'VCM',
    group: 'Eritrograma e Leucograma',
    ranges: {
      TRIM1: { min: 88, max: 92, display: '88 - 92fl', unit: 'fl' },
      TRIM2: { min: 88, max: 92, display: '88 - 92fl', unit: 'fl' },
      TRIM3: { min: 88, max: 92, display: '88 - 92fl', unit: 'fl' },
    },
  },
  {
    id: 'chcm',
    name: 'CHCM',
    group: 'Eritrograma e Leucograma',
    ranges: {
      TRIM1: { min: 31, max: 35, display: '31 - 35g/dL', unit: 'g/dL' },
      TRIM2: { min: 31, max: 35, display: '31 - 35g/dL', unit: 'g/dL' },
      TRIM3: { min: 31, max: 35, display: '31 - 35g/dL', unit: 'g/dL' },
    },
  },
  {
    id: 'rdw',
    name: 'RDW',
    group: 'Eritrograma e Leucograma',
    ranges: {
      TRIM1: { min: 11.5, max: 13, display: '11,5-13%', unit: '%' },
      TRIM2: { min: 11.5, max: 13, display: '11,5-13%', unit: '%' },
      TRIM3: { min: 11.5, max: 13, display: '11,5-13%', unit: '%' },
    },
  },
  {
    id: 'leucocitos',
    name: 'Leucócitos',
    group: 'Eritrograma e Leucograma',
    ranges: {
      TRIM1: { min: 4000, max: 13000, display: '4000-13.000mm³', unit: 'mm³' },
      TRIM2: { min: 4000, max: 13000, display: '4000-13.000mm³', unit: 'mm³' },
      TRIM3: { min: 4000, max: 13000, display: '4000-13.000mm³', unit: 'mm³' },
    },
  },
  {
    id: 'eosinofilos',
    name: 'Eosinófilos',
    group: 'Eritrograma e Leucograma',
    ranges: {
      TRIM1: { max: 1, display: '< 1%', unit: '%' },
      TRIM2: { max: 1, display: '< 1%', unit: '%' },
      TRIM3: { max: 1, display: '< 1%', unit: '%' },
    },
  },
  {
    id: 'linfocitos',
    name: 'Linfócitos',
    group: 'Eritrograma e Leucograma',
    ranges: {
      TRIM1: { min: 25, max: 35, display: '25 - 35%', unit: '%' },
      TRIM2: { min: 25, max: 35, display: '25 - 35%', unit: '%' },
      TRIM3: { min: 25, max: 35, display: '25 - 35%', unit: '%' },
    },
  },
  {
    id: 'monocitos',
    name: 'Monócitos',
    group: 'Eritrograma e Leucograma',
    ranges: {
      TRIM1: { min: 3, max: 8, display: '3 - 8 %', unit: '%' },
      TRIM2: { min: 3, max: 8, display: '3 - 8 %', unit: '%' },
      TRIM3: { min: 3, max: 8, display: '3 - 8 %', unit: '%' },
    },
  },
  {
    id: 'plaquetas',
    name: 'Plaquetas',
    group: 'Eritrograma e Leucograma',
    obs: 'é comum a queda de plaquetas ao longo dos trimestres',
    ranges: {
      TRIM1: { min: 150, max: 300, display: '150 mil - 300 mil' },
      TRIM2: { min: 150, max: 300, display: '150 mil - 300 mil' },
      TRIM3: { min: 150, max: 300, display: '150 mil - 300 mil' },
    },
  },
  {
    id: '25-odd3',
    name: '25 (OD)D3',
    group: 'Nutricional',
    ranges: {
      TRIM1: { min: 40, display: '> 40ng/ml', unit: 'ng/ml' },
      TRIM2: { min: 40, display: '> 40ng/ml', unit: 'ng/ml' },
      TRIM3: { min: 40, display: '> 40ng/ml', unit: 'ng/ml' },
    },
  },
  {
    id: 'folato-serico',
    name: 'Folato sérico',
    group: 'Nutricional',
    obs: 'normal estar > se c/ suplem.',
    ranges: {
      TRIM1: { min: 10, display: '> 10ng/mL' },
      TRIM2: { min: 10, display: '> 10ng/mL' },
      TRIM3: { min: 10, display: '> 10ng/mL' },
    },
  },
  {
    id: 'folato-eritrocitario',
    name: 'Folato eritrocitário',
    group: 'Nutricional',
    ranges: {
      TRIM1: { min: 600, display: '> 600ng/mL', unit: 'ng/mL' },
      TRIM2: { min: 600, display: '> 600ng/mL', unit: 'ng/mL' },
      TRIM3: { min: 600, display: '> 600ng/mL', unit: 'ng/mL' },
    },
  },
  {
    id: 'vitamina-b12',
    name: 'Vitamina B12',
    group: 'Nutricional',
    ranges: {
      TRIM1: { min: 500, max: 800, display: '500 - 800 pg/mL', unit: 'pg/mL' },
      TRIM2: { min: 500, max: 800, display: '500 - 800 pg/mL', unit: 'pg/mL' },
      TRIM3: { min: 500, max: 800, display: '500 - 800 pg/mL', unit: 'pg/mL' },
    },
  },
  {
    id: 'acido-metilmalonico',
    name: 'Ácido metilmalônico',
    group: 'Nutricional',
    ranges: {
      TRIM1: { max: 0.5, display: '< 0,5mcmol/L' },
      TRIM2: { max: 0.5, display: '< 0,5mcmol/L' },
      TRIM3: { max: 0.5, display: '< 0,5mcmol/L' },
    },
  },
  {
    id: 'vitamina-b6-serica',
    name: 'Vitamina B6 sérica',
    group: 'Nutricional',
    ranges: {
      TRIM1: { min: 50, display: '> 50mcg/L', unit: 'mcg/L' },
      TRIM2: { min: 50, display: '> 50mcg/L', unit: 'mcg/L' },
      TRIM3: { min: 50, display: '> 50mcg/L', unit: 'mcg/L' },
    },
  },
  {
    id: 'homocisteina',
    name: 'Homocisteína',
    group: 'Nutricional',
    obs: 'acompanhar por trimestre',
    ranges: {
      TRIM1: { min: 3.7, max: 6.9, display: '3,7-6,9 mcmol/L', unit: 'mcmol/L' },
      TRIM2: { min: 2.7, max: 7.2, display: '2,7 - 7,2 mcmol/L', unit: 'mcmol/L' },
      TRIM3: { min: 2.4, max: 8, display: '2,4 - 8 mcmol/L', unit: 'mcmol/L' },
    },
  },
  {
    id: 'zinco-serico',
    name: 'Zinco sérico',
    group: 'Nutricional',
    ranges: {
      TRIM1: { min: 90, max: 110, display: '90 - 110 mcg/dL', unit: 'mcg/dL' },
      TRIM2: { min: 90, max: 110, display: '90 - 110 mcg/dL', unit: 'mcg/dL' },
      TRIM3: { min: 90, max: 110, display: '90 - 110 mcg/dL', unit: 'mcg/dL' },
    },
  },
  {
    id: 'selenio-serico',
    name: 'Selênio sérico',
    group: 'Nutricional',
    ranges: {
      TRIM1: { min: 95, max: 190, display: '95 - 190 mcg/L', unit: 'mcg/L' },
      TRIM2: { min: 95, max: 190, display: '95 - 190 mcg/L', unit: 'mcg/L' },
      TRIM3: { min: 95, max: 190, display: '95 - 190 mcg/L', unit: 'mcg/L' },
    },
  },
  {
    id: 'cobre-serico',
    name: 'Cobre sérico',
    group: 'Nutricional',
    obs: 'Avaliar valor de referência do laboratório para gestantes',
    ranges: {
      TRIM1: { min: 90, max: 125, display: '90 a 125mcg/dL', unit: 'mcg/dL' },
      TRIM2: { min: 90, max: 125, display: '90 a 125mcg/dL', unit: 'mcg/dL' },
      TRIM3: { min: 90, max: 125, display: '90 a 125mcg/dL', unit: 'mcg/dL' },
    },
  },
  {
    id: 'vitamina-a',
    name: 'Vitamina A',
    group: 'Nutricional',
    ranges: {
      TRIM1: { min: 0.4, max: 0.7, display: '0,4 a 0,7 mg/L', unit: 'mg/L' },
      TRIM2: { min: 0.4, max: 0.7, display: '0,4 a 0,7 mg/L', unit: 'mg/L' },
      TRIM3: { min: 0.4, max: 0.7, display: '0,4 a 0,7 mg/L', unit: 'mg/L' },
    },
  },
  {
    id: 'calcio-ionico',
    name: 'Cálcio iônico',
    group: 'Nutricional',
    ranges: {
      TRIM1: { min: 1.1, max: 1.25, display: '1,1 a 1,25 mmol/L', unit: 'mmol/L' },
      TRIM2: { min: 1.1, max: 1.25, display: '1,1 a 1,25 mmol/L', unit: 'mmol/L' },
      TRIM3: { min: 1.1, max: 1.25, display: '1,1 a 1,25 mmol/L', unit: 'mmol/L' },
    },
  },
  {
    id: 'calcio-serico',
    name: 'Cálcio sérico',
    group: 'Nutricional',
    ranges: {
      TRIM1: { min: 8.6, max: 10, display: '8,6 – 10,0 mg/dL', unit: 'mg/dL' },
      TRIM2: { min: 8.6, max: 10, display: '8,6 – 10,0 mg/dL', unit: 'mg/dL' },
      TRIM3: { min: 8.6, max: 10, display: '8,6 – 10,0 mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'calcio-urinario-24-horas',
    name: 'Cálcio urinário 24 horas',
    group: 'Nutricional',
    ranges: {
      TRIM1: { min: 350, max: 620, display: '350 - 620mg /24hs' },
      TRIM2: { min: 350, max: 620, display: '350 - 620mg /24hs' },
      TRIM3: { min: 350, max: 620, display: '350 - 620mg /24hs' },
    },
  },
  {
    id: 'magnesio-serico',
    name: 'Magnésio sérico',
    group: 'Nutricional',
    ranges: {
      TRIM1: { min: 2, max: 2.2, display: '2,0 - 2,2 mg/dL', unit: 'mg/dL' },
      TRIM2: { min: 2, max: 2.2, display: '2,0 - 2,2 mg/dL', unit: 'mg/dL' },
      TRIM3: { min: 2, max: 2.2, display: '2,0 - 2,2 mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'magnesio-eritrocitario',
    name: 'Magnésio eritrocitário',
    group: 'Nutricional',
    ranges: {
      TRIM1: { min: 4.8, max: 6, display: '4,8 – 6,0 mg/dL', unit: 'mg/dL' },
      TRIM2: { min: 4.8, max: 6, display: '4,8 – 6,0 mg/dL', unit: 'mg/dL' },
      TRIM3: { min: 4.8, max: 6, display: '4,8 – 6,0 mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'ferritina',
    name: 'Ferritina',
    group: 'Avaliação do ferro',
    obs: 'acompanhar com PCR',
    ranges: {
      TRIM1: { min: 70, display: '>70 ng/mL', unit: 'ng/mL' },
      TRIM2: { min: 70, display: '>70 ng/mL', unit: 'ng/mL' },
      TRIM3: { min: 70, display: '>70 ng/mL', unit: 'ng/mL' },
    },
  },
  {
    id: 'pcr-us',
    name: 'PCR- us',
    group: 'Avaliação do ferro',
    ranges: {
      TRIM1: { max: 1, display: '< 1,0 mg/L', unit: 'mg/L' },
      TRIM2: { max: 1, display: '< 1,0 mg/L', unit: 'mg/L' },
      TRIM3: { max: 1, display: '< 1,0 mg/L', unit: 'mg/L' },
    },
  },
  {
    id: 'saturacao-de-transferrina',
    name: 'Saturação de transferrina',
    group: 'Avaliação do ferro',
    ranges: {
      TRIM1: { min: 25, max: 35, display: '25-35%', unit: '%' },
      TRIM2: { min: 25, max: 35, display: '25-35%', unit: '%' },
      TRIM3: { min: 25, max: 35, display: '25-35%', unit: '%' },
    },
  },
  {
    id: 'ret-he',
    name: 'Ret-He',
    group: 'Avaliação do ferro',
    ranges: {
      TRIM1: { min: 29, display: '>29pg', unit: 'pg' },
      TRIM2: { min: 29, display: '>29pg', unit: 'pg' },
      TRIM3: { min: 29, display: '>29pg', unit: 'pg' },
    },
  },
  {
    id: 'chumbo',
    name: 'Chumbo',
    group: 'Metais pesados',
    ranges: {
      TRIM1: { max: 3, display: '< 3,0 mcg/L', unit: 'mcg/L' },
      TRIM2: { max: 3, display: '< 3,0 mcg/L', unit: 'mcg/L' },
      TRIM3: { max: 3, display: '< 3,0 mcg/L', unit: 'mcg/L' },
    },
  },
  {
    id: 'cadmio',
    name: 'Cádmio',
    group: 'Metais pesados',
    ranges: {
      TRIM1: { max: 0.5, display: '< 0,5 mcg/L', unit: 'mcg/L' },
      TRIM2: { max: 0.5, display: '< 0,5 mcg/L', unit: 'mcg/L' },
      TRIM3: { max: 0.5, display: '< 0,5 mcg/L', unit: 'mcg/L' },
    },
  },
  {
    id: 'aluminio',
    name: 'Alumínio',
    group: 'Metais pesados',
    ranges: {
      TRIM1: { max: 2, display: '< 2,0 mcg/L', unit: 'mcg/L' },
      TRIM2: { max: 2, display: '< 2,0 mcg/L', unit: 'mcg/L' },
      TRIM3: { max: 2, display: '< 2,0 mcg/L', unit: 'mcg/L' },
    },
  },
  {
    id: 'mercurio',
    name: 'Mercúrio',
    group: 'Metais pesados',
    ranges: {
      TRIM1: { max: 1, display: '< 1,0 mcg/L', unit: 'mcg/L' },
      TRIM2: { max: 1, display: '< 1,0 mcg/L', unit: 'mcg/L' },
      TRIM3: { max: 1, display: '< 1,0 mcg/L', unit: 'mcg/L' },
    },
  },
  {
    id: 'arsenico',
    name: 'Arsênico',
    group: 'Metais pesados',
    ranges: {
      TRIM1: { max: 10, display: '< 10,0 mcg/L', unit: 'mcg/L' },
      TRIM2: { max: 10, display: '< 10,0 mcg/L', unit: 'mcg/L' },
      TRIM3: { max: 10, display: '< 10,0 mcg/L', unit: 'mcg/L' },
    },
  },
  {
    id: 'glicemia-jejum',
    name: 'Glicemia jejum',
    group: 'Glicêmico',
    ranges: {
      TRIM1: { min: 70, max: 85, display: '70-85mg/dL', unit: 'mg/dL' },
      TRIM2: { min: 70, max: 85, display: '70-85mg/dL', unit: 'mg/dL' },
      TRIM3: { min: 70, max: 85, display: '70-85mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'insulina-jejum',
    name: 'Insulina jejum',
    group: 'Glicêmico',
    ranges: {
      TRIM2: { display: 'entre 2 e 6 µIU/mL', unit: 'µIU/mL' },
      TRIM3: { max: 10, display: '< 10µU/mL', unit: 'µU/mL' },
      TRIM1: { display: 'entre 2 e 6 µIU/mL', unit: 'µIU/mL' },
    },
  },
  {
    id: 'glicemia-pos-prandial',
    name: 'Glicemia pós prandial',
    group: 'Glicêmico',
    obs: '2h após almoço',
    ranges: {
      TRIM1: { max: 100, display: '< 100 a 110mg/dL', unit: 'mg/dL' },
      TRIM2: { max: 100, display: '< 100 a 110mg/dL', unit: 'mg/dL' },
      TRIM3: { max: 100, display: '< 100 a 110mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'insulina-pos-prandial',
    name: 'Insulina pós prandial',
    group: 'Glicêmico',
    obs: '2h após almoço',
    ranges: {
      TRIM1: { min: 10, max: 25, display: '10-25µU/mL', unit: 'µU/mL' },
      TRIM2: { min: 10, max: 25, display: '10-25µU/mL', unit: 'µU/mL' },
      TRIM3: { min: 10, max: 25, display: '10-25µU/mL', unit: 'µU/mL' },
    },
  },
  {
    id: 'hemoglobina-glicada',
    name: 'Hemoglobina glicada',
    group: 'Glicêmico',
    obs: 'de acordo com id. gestacional',
    ranges: {
      TRIM1: { min: 3.9, max: 5, display: '3,9-5,0%', unit: '%' },
      TRIM2: { min: 3.5, max: 5.9, display: '3,5-5,9%', unit: '%' },
      TRIM3: { min: 3.5, max: 5.9, display: '3,5-5,9%', unit: '%' },
    },
  },
  {
    id: 'cortisol',
    name: 'Cortisol (até 8:30h)',
    group: 'Tireoide e hormonal',
    ranges: {
      TRIM1: { min: 10, max: 20, display: '10 - 20 mcg/dL', unit: 'mcg/dL' },
      TRIM2: { min: 10, max: 20, display: '10 - 20 mcg/dL', unit: 'mcg/dL' },
      TRIM3: { min: 10, max: 20, display: '10 - 20 mcg/dL', unit: 'mcg/dL' },
    },
  },
  {
    id: 'tsh',
    name: 'TSH',
    group: 'Tireoide e hormonal',
    ranges: {
      TRIM1: { min: 0.15, max: 1.5, display: '0,15- 1,5mIU/L', unit: 'mIU/L' },
      TRIM2: { min: 0.4, max: 2, display: '0,4- 2mIU/L', unit: 'mIU/L' },
      TRIM3: { min: 0.15, max: 2, display: '0,15-2mIU/L', unit: 'mIU/L' },
    },
  },
  {
    id: 't3-livre',
    name: 'T3 livre',
    group: 'Tireoide e hormonal',
    ranges: {
      TRIM1: { min: 3, max: 4, display: '3,0 – 4ng/mL', unit: 'ng/mL' },
      TRIM2: { min: 3, max: 4, display: '3,0 – 4ng/mL', unit: 'ng/mL' },
      TRIM3: { min: 3, max: 4, display: '3,0 – 4ng/mL', unit: 'ng/mL' },
    },
  },
  {
    id: 't4-livre',
    name: 'T4 livre',
    group: 'Tireoide e hormonal',
    ranges: {
      TRIM1: { min: 0.9, max: 1.3, display: '0,9 - 1,3 ng/mL', unit: 'ng/mL' },
      TRIM2: { min: 0.9, max: 1.3, display: '0,9 - 1,3 ng/mL', unit: 'ng/mL' },
      TRIM3: { min: 0.9, max: 1.3, display: '0,9 - 1,3 ng/mL', unit: 'ng/mL' },
    },
  },
  {
    id: 'anti-tireoglobulina',
    name: 'Anti tireoglobulina',
    group: 'Tireoide e hormonal',
    obs: 'o importante é ser < que o VR',
    ranges: {
      TRIM1: { display: '< VR do lab.' },
      TRIM2: { display: '< VR do lab.' },
      TRIM3: { display: '< VR do lab.' },
    },
  },
  {
    id: 'anti-tireoperoxidase',
    name: 'Anti tireoperoxidase',
    group: 'Tireoide e hormonal',
    ranges: {
      TRIM1: { display: '< VR do lab.' },
      TRIM2: { display: '< VR do lab.' },
      TRIM3: { display: '< VR do lab.' },
    },
  },
  {
    id: 'iodo-urinario-24h',
    name: 'Iodo urinário 24h',
    group: 'Tireoide e hormonal',
    obs: 'Muda muito com o laboratório',
    ranges: {
      TRIM1: { display: 'Terço superior do VR' },
      TRIM2: { display: 'Terço superior do VR' },
      TRIM3: { display: 'Terço superior do VR' },
    },
  },
  {
    id: 'proteinas-total',
    name: 'Proteínas total',
    group: 'Avaliação metabólica e hepática',
    ranges: {
      TRIM1: { min: 6.4, max: 8.2, display: '6,4 a 8,2 g/dl', unit: 'g/dl' },
      TRIM2: { min: 6.4, max: 8.2, display: '6,4 a 8,2 g/dl', unit: 'g/dl' },
      TRIM3: { min: 6.4, max: 8.2, display: '6,4 a 8,2 g/dl', unit: 'g/dl' },
    },
  },
  {
    id: 'albumina',
    name: 'Albumina',
    group: 'Avaliação metabólica e hepática',
    obs: 'acompanhar evolução da própria gestante',
    ranges: {
      TRIM1: { min: 3.5, max: 5.2, display: '3,5 a 5,2 g/dl', unit: 'g/dl' },
      TRIM2: { min: 3.5, max: 5.2, display: '3,5 a 5,2 g/dl', unit: 'g/dl' },
      TRIM3: { min: 3.5, max: 5.2, display: '3,5 a 5,2 g/dl', unit: 'g/dl' },
    },
  },
  {
    id: 'acido-urico',
    name: 'Ácido úrico',
    group: 'Avaliação metabólica e hepática',
    ranges: {
      TRIM1: { max: 3.9, display: '< 3,9 mg/dL', unit: 'mg/dL' },
      TRIM2: { max: 3.9, display: '< 3,9 mg/dL', unit: 'mg/dL' },
      TRIM3: { max: 3.9, display: '< 3,9 mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'tgo',
    name: 'TGO',
    group: 'Avaliação metabólica e hepática',
    ranges: {
      TRIM1: { min: 10, max: 20, display: '10 - 20U/L', unit: 'U/L' },
      TRIM2: { min: 10, max: 20, display: '10 - 20U/L', unit: 'U/L' },
      TRIM3: { min: 10, max: 20, display: '10 - 20U/L', unit: 'U/L' },
    },
  },
  {
    id: 'tgp',
    name: 'TGP',
    group: 'Avaliação metabólica e hepática',
    ranges: {
      TRIM1: { min: 10, max: 20, display: '10 - 20U/L', unit: 'U/L' },
      TRIM2: { min: 10, max: 20, display: '10 - 20U/L', unit: 'U/L' },
      TRIM3: { min: 10, max: 20, display: '10 - 20U/L', unit: 'U/L' },
    },
  },
  {
    id: 'ggt',
    name: 'GGT',
    group: 'Avaliação metabólica e hepática',
    ranges: {
      TRIM1: { min: 10, max: 20, display: '10 - 20U/L', unit: 'U/L' },
      TRIM2: { min: 10, max: 20, display: '10 - 20U/L', unit: 'U/L' },
      TRIM3: { min: 10, max: 20, display: '10 - 20U/L', unit: 'U/L' },
    },
  },
]
